import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Tag as TagIcon, Trash2, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
const tagColors = [{
  name: 'blue',
  bg: 'bg-blue-50',
  text: 'text-blue-700',
  border: 'border-blue-200'
}, {
  name: 'green',
  bg: 'bg-green-50',
  text: 'text-green-700',
  border: 'border-green-200'
}, {
  name: 'purple',
  bg: 'bg-purple-50',
  text: 'text-purple-700',
  border: 'border-purple-200'
}, {
  name: 'orange',
  bg: 'bg-orange-50',
  text: 'text-orange-700',
  border: 'border-orange-200'
}, {
  name: 'red',
  bg: 'bg-red-50',
  text: 'text-red-700',
  border: 'border-red-200'
}, {
  name: 'yellow',
  bg: 'bg-yellow-50',
  text: 'text-yellow-700',
  border: 'border-yellow-200'
}, {
  name: 'pink',
  bg: 'bg-pink-50',
  text: 'text-pink-700',
  border: 'border-pink-200'
}, {
  name: 'gray',
  bg: 'bg-gray-50',
  text: 'text-gray-700',
  border: 'border-gray-200'
}];
export function Tags() {
  const {
    tags,
    submissions,
    createTag,
    deleteTag,
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    color: 'blue',
    description: ''
  });
  const getTagColor = (colorName: string) => {
    return tagColors.find(c => c.name === colorName) || tagColors[0];
  };
  const getSubmissionCount = (tagName: string) => {
    return submissions.filter(s => s.tags?.includes(tagName)).length;
  };
  const handleCreateTag = () => {
    if (newTag.name.trim()) {
      createTag({
        workspaceId: currentWorkspace.id,
        subRegion: currentSubRegion || undefined,
        name: newTag.name.trim().toLowerCase(),
        color: newTag.color,
        description: newTag.description
      });
      setNewTag({
        name: '',
        color: 'blue',
        description: ''
      });
      setShowCreateModal(false);
    }
  };
  return <div className="space-y-6" data-id="element-2057">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2058">
        <div className="min-w-0" data-id="element-2059">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2060">Tags</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2061">
            Manage tags for segmenting submissions and subscribers.
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-2062">
          <Plus className="h-4 w-4 flex-shrink-0" data-id="element-2063" />
          <span data-id="element-2064">Create Tag</span>
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2065">
        {tags.length === 0 ? <div className="p-12 text-center" data-id="element-2066">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-2067" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2068">
              No tags created
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2069">
              Create tags to segment your submissions and subscribers.
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-2070">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2071">
              <thead className="bg-gray-50" data-id="element-2072">
                <tr data-id="element-2073">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2074">
                    Tag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2075">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2076">
                    Submissions
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-2077">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2078">
                {tags.map(tag => {
              const color = getTagColor(tag.color);
              const count = getSubmissionCount(tag.name);
              return <tr key={tag.id} className="hover:bg-gray-50" data-id="element-2079">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2080">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${color.bg} ${color.text} border ${color.border} whitespace-nowrap`} data-id="element-2081">
                          <TagIcon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-2082" />
                          {tag.name}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2083">
                        <span className="text-sm text-gray-500 truncate block max-w-[200px]" data-id="element-2084">
                          {tag.description || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2085">
                        <Link to={`/tags/${tag.name}`} className="text-sm font-medium text-blue-600 hover:text-blue-900 whitespace-nowrap" data-id="element-2086">
                          {count} submission{count !== 1 ? 's' : ''}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2087">
                        <button onClick={() => deleteTag(tag.id)} className="text-red-600 hover:text-red-900 flex-shrink-0" data-id="element-2088">
                          <Trash2 className="h-4 w-4" data-id="element-2089" />
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create Tag Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2090">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-2091">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} data-id="element-2092" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-2093">
              <div className="flex items-center justify-between mb-4" data-id="element-2094">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2095">
                  Create New Tag
                </h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500 flex-shrink-0" data-id="element-2096">
                  <X className="h-5 w-5" data-id="element-2097" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-2098">
                <div data-id="element-2099">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2100">
                    Tag Name
                  </label>
                  <input type="text" placeholder="e.g., paid-ads, referral, promo-2024" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTag.name} onChange={e => setNewTag({
                ...newTag,
                name: e.target.value
              })} data-id="element-2101" />
                </div>

                <div data-id="element-2102">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2103">
                    Color
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-2104">
                    {tagColors.map(color => <button key={color.name} type="button" onClick={() => setNewTag({
                  ...newTag,
                  color: color.name
                })} className={`w-8 h-8 rounded-full ${color.bg} border-2 flex-shrink-0 ${newTag.color === color.name ? 'border-gray-900' : 'border-transparent'}`} data-id="element-2105" />)}
                  </div>
                </div>

                <div data-id="element-2106">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2107">
                    Description (Optional)
                  </label>
                  <input type="text" placeholder="What is this tag used for?" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTag.description} onChange={e => setNewTag({
                ...newTag,
                description: e.target.value
              })} data-id="element-2108" />
                </div>

                <div className="flex justify-end gap-3 pt-4" data-id="element-2109">
                  <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-2110">
                    Cancel
                  </button>
                  <button onClick={handleCreateTag} disabled={!newTag.name.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap" data-id="element-2111">
                    Create Tag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}